import React, { createContext, useEffect, useState } from 'react';
import { BrowserRouter as Router, Routes, Route } from 'react-router-dom';
import { onAuthStateChanged, User } from 'firebase/auth';
import { auth } from './services/firebase';
import Header from './components/Header';
import Footer from './components/Footer';
import HomePage from './components/HomePage';
import ProjectsPage from './components/ProjectsPage';
import ProjectDetailPage from './components/ProjectDetailPage';
import ArticlesPage from './components/ArticlesPage';
import BlogPage from './components/BlogPage';
import AdminDashboard from './components/AdminDashboard';
import AdminBlogPage from './components/AdminBlogPage';
import AdminBlogEditor from './components/AdminBlogEditor';
import AdminProjectsPage from './components/AdminProjectsPage';
import AdminProjectEditor from './components/AdminProjectEditor';
import AdminLogin from './components/AdminLogin';
import ProtectedRoute from './components/ProtectedRoute';

import TickerBanner from './components/TickerBanner';

// Theme context
interface ThemeContextType {
  theme: string;
  toggleTheme: () => void;
}

export const ThemeContext = createContext<ThemeContextType>({
  theme: 'dark',
  toggleTheme: () => { },
});

// Auth context
interface AuthContextType {
  user: User | null;
  loading: boolean;
}

export const AuthContext = createContext<AuthContextType>({
  user: null,
  loading: true,
});

function App() {
  const [theme, setTheme] = useState(() => {
    return localStorage.getItem('theme') || 'dark';
  });

  const [user, setUser] = useState<User | null>(null);
  const [authLoading, setAuthLoading] = useState(true);

  useEffect(() => {
    if (theme === 'dark') {
      document.documentElement.classList.add('dark');
    } else {
      document.documentElement.classList.remove('dark');
    }
    localStorage.setItem('theme', theme);
  }, [theme]);

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (user) => {
      setUser(user);
      setAuthLoading(false);
    });
    return () => unsubscribe();
  }, []);

  const toggleTheme = () => {
    setTheme((prev) => (prev === 'dark' ? 'light' : 'dark'));
  };

  return (
    <ThemeContext.Provider value={{ theme, toggleTheme }}>
      <AuthContext.Provider value={{ user, loading: authLoading }}>
        {/* Dot Grid Background */}
        <div className="dot-grid" />

        <div className="relative min-h-screen flex flex-col">
          <Router>
            <TickerBanner />
            <Header />
            <main className="flex-1">
              <Routes>
                {/* Public routes */}
                <Route path="/" element={<HomePage />} />
                <Route path="/projects" element={<ProjectsPage />} />
                <Route path="/projects/:id" element={<ProjectDetailPage />} />
                <Route path="/articles" element={<ArticlesPage />} />
                <Route path="/blog/:id" element={<BlogPage />} />

                {/* Admin routes */}
                <Route path="/admin/login" element={<AdminLogin />} />
                <Route
                  path="/admin"
                  element={
                    <ProtectedRoute>
                      <AdminDashboard />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/admin/articles"
                  element={
                    <ProtectedRoute>
                      <AdminBlogPage />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/admin/articles/new"
                  element={
                    <ProtectedRoute>
                      <AdminBlogEditor mode="create" />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/admin/articles/edit/:id"
                  element={
                    <ProtectedRoute>
                      <AdminBlogEditor mode="edit" />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/admin/projects"
                  element={
                    <ProtectedRoute>
                      <AdminProjectsPage />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/admin/projects/new"
                  element={
                    <ProtectedRoute>
                      <AdminProjectEditor mode="create" />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/admin/projects/edit/:id"
                  element={
                    <ProtectedRoute>
                      <AdminProjectEditor mode="edit" />
                    </ProtectedRoute>
                  }
                />
              </Routes>
            </main>
            <Footer />
          </Router>
        </div>
      </AuthContext.Provider>
    </ThemeContext.Provider>
  );
}

export default App;